/**
 * QC Settings — Phase 12 (Admin only)
 *
 * Backend:
 *   GET    /api/qc/settings/deadlines           list OTA-specific deadlines
 *   POST   /api/qc/settings/deadlines           upsert (ota_name, deadline_minutes)
 *   DELETE /api/qc/settings/deadlines/{ota}
 *   GET    /api/qc/settings/thresholds          { late_min, urgent_min, critical_min }
 *   PUT    /api/qc/settings/thresholds          update thresholds
 *
 * Layout:
 *   • OTA Deadlines table — admin can add/edit/delete a per-OTA deadline (minutes).
 *   • Overdue Thresholds form — Late / Urgent / Critical (minutes from deadline).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Plus, Trash2, Save, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const QCSettings = () => {
  const [deadlines, setDeadlines] = useState([]);
  const [thresholds, setThresholds] = useState({ late_min: 60, urgent_min: 120, critical_min: 180 });
  const [loading, setLoading] = useState(true);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [savingRow, setSavingRow] = useState(null);
  const [newOta, setNewOta] = useState('');
  const [newMins, setNewMins] = useState(120);

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, tRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/settings/deadlines`, { headers: authHeader() }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/settings/thresholds`, { headers: authHeader() }),
      ]);
      if (!dRes.ok) throw new Error(`Deadlines HTTP ${dRes.status}`);
      if (!tRes.ok) throw new Error(`Thresholds HTTP ${tRes.status}`);
      setDeadlines(await dRes.json());
      setThresholds(await tRes.json());
    } catch (e) { toast.error(e.message || 'Failed to load QC settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsertDeadline = async (ota_name, deadline_minutes) => {
    setSavingRow(ota_name);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/settings/deadlines`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ota_name, deadline_minutes }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success(`Saved ${ota_name}`);
      await load();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSavingRow(null); }
  };

  const deleteDeadline = async (ota_name) => {
    if (!window.confirm(`Delete deadline for ${ota_name}?`)) return;
    setSavingRow(ota_name);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/settings/deadlines/${encodeURIComponent(ota_name)}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${ota_name} removed`);
      await load();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
    finally { setSavingRow(null); }
  };

  const addNew = async () => {
    const name = newOta.trim();
    if (!name) { toast.error('Enter an OTA name'); return; }
    const mins = parseInt(newMins, 10);
    if (isNaN(mins) || mins <= 0) { toast.error('Enter valid minutes'); return; }
    await upsertDeadline(name, mins);
    setNewOta('');
    setNewMins(120);
  };

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/settings/thresholds`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(thresholds),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const t = await res.json();
      setThresholds(t);
      toast.success('Thresholds saved');
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSavingThresholds(false); }
  };

  return (
    <div className="space-y-6" data-testid="qc-settings-page">
      <div>
        <h3 className="text-lg font-bold text-gray-800 border-b pb-2">QC Settings</h3>
        <p className="text-sm text-gray-500 mt-2">Configure per-OTA QC deadlines and the Late/Urgent/Critical overdue thresholds used across CX Leader & QC dashboards.</p>
      </div>

      {/* === OTA Deadlines === */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <Clock size={18} className="text-fuchsia-600" />
          <h4 className="font-semibold text-gray-800">OTA Deadlines</h4>
          <span className="text-xs text-gray-400 ml-auto">Default: 120 minutes when not set</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="qc-deadlines-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2.5 text-left">OTA Name</th>
                <th className="px-4 py-2.5 text-right">Deadline (minutes)</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400 text-xs"><Loader2 size={16} className="inline animate-spin" /> Loading…</td></tr>
              )}
              {!loading && deadlines.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400 text-xs">No custom deadlines yet — add your first OTA below.</td></tr>
              )}
              {deadlines.map((d) => (
                <DeadlineRow key={d.ota_name}
                  row={d}
                  busy={savingRow === d.ota_name}
                  onSave={(mins) => upsertDeadline(d.ota_name, mins)}
                  onDelete={() => deleteDeadline(d.ota_name)}
                />
              ))}
              <tr className="bg-gray-50/60">
                <td className="px-4 py-3">
                  <input type="text" value={newOta} onChange={(e) => setNewOta(e.target.value)} placeholder="e.g. Booking.com"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="qc-deadline-new-ota" />
                </td>
                <td className="px-4 py-3 text-right">
                  <input type="number" min={1} value={newMins} onChange={(e) => setNewMins(e.target.value)}
                    className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-teal-500 outline-none"
                    data-testid="qc-deadline-new-mins" />
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={addNew} className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-md text-xs hover:bg-teal-700" data-testid="qc-deadline-add">
                    <Plus size={12} /> Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* === Overdue Thresholds === */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle size={18} className="text-amber-600" />
          <h4 className="font-semibold text-gray-800">Overdue Thresholds (minutes past deadline)</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ThresholdInput label="Late (min)" value={thresholds.late_min} onChange={(v) => setThresholds({ ...thresholds, late_min: v })} testId="qc-threshold-late" />
          <ThresholdInput label="Urgent (min)" value={thresholds.urgent_min} onChange={(v) => setThresholds({ ...thresholds, urgent_min: v })} testId="qc-threshold-urgent" />
          <ThresholdInput label="Critical (min)" value={thresholds.critical_min} onChange={(v) => setThresholds({ ...thresholds, critical_min: v })} testId="qc-threshold-critical" />
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={saveThresholds} disabled={savingThresholds}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50"
            data-testid="qc-thresholds-save">
            {savingThresholds ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Thresholds
          </button>
        </div>
      </div>
    </div>
  );
};

const DeadlineRow = ({ row, busy, onSave, onDelete }) => {
  const [mins, setMins] = useState(row.deadline_minutes);
  const changed = parseInt(mins, 10) !== row.deadline_minutes;
  return (
    <tr className="hover:bg-gray-50" data-testid={`qc-deadline-row-${row.ota_name}`}>
      <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.ota_name}</td>
      <td className="px-4 py-3 text-right">
        <input type="number" min={1} value={mins} onChange={(e) => setMins(e.target.value)}
          className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-teal-500 outline-none"
          data-testid={`qc-deadline-mins-${row.ota_name}`} />
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          <button onClick={() => onSave(parseInt(mins, 10))} disabled={!changed || busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-40"
            data-testid={`qc-deadline-save-${row.ota_name}`}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
          </button>
          <button onClick={onDelete} disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-rose-50 text-rose-700 rounded-md hover:bg-rose-100 disabled:opacity-40"
            data-testid={`qc-deadline-delete-${row.ota_name}`}>
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
};

const ThresholdInput = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
    <input type="number" min={1} value={value ?? ''} onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
      data-testid={testId} />
  </div>
);

export default QCSettings;
